/**
 * `compileTransaction`: turns a plain-language `TransactionInput` into a
 * balanced `VerifikationDraft` plus the immutable `meta`, following the
 * compile table in docs/product-spec.md section 10. Pure and entity-exhaustive;
 * `validateVerifikation` / `bookVerifikation` still have the last word.
 *
 * Amounts are öre. `input.amountOre` is the gross amount and always positive.
 * Ledger wording (debit/credit, account numbers) appears only in the
 * `explanation`, which is Layer-1 copy for "Visa bokföringsdetaljer".
 */
import { representationVatCapOre } from '../bas/constants';
import {
  INPUT_VAT_ACCOUNT,
  REVERSE_CHARGE_INPUT_VAT_ACCOUNT,
  VAT_SETTLEMENT_ACCOUNT,
  baseMomskodFor,
  isReverseCharge,
  isVatClearingAccount,
  outputVatAccountFor,
  reverseChargeOutputAccountFor,
} from '../bas/momskoder';
import { basAccountName } from '../bas';
import { REPRESENTATION_NON_DEDUCTIBLE_ACCOUNT, assetDecision, findCategory, resolveCategoryAccount, type AssetDecision, type ResolvedAccount } from '../catalog/rules';
import type { Category } from '../catalog/types';
import { formatSek } from '../money';
import type { Ore } from '../money';
import { ownerAccountFor } from '../payment-sources/rules';
import type {
  EntityType,
  PaymentSource,
  TaxSubkind,
  TransactionKind,
  VatChoice,
  VatRate,
  VerifikationMeta,
  VerifikationRow,
} from '../types';
import { reverseRows, type VerifikationDraft } from '../verifikationer/rules';
import { CompileError, type CompileContext, type CompileResult, type TransactionInput } from './types';
import { splitGross, vatFromGross, vatOnNet } from './vat';

export const SUPPLIER_INVOICE_ACCOUNT = 2440;
export const CUSTOMER_INVOICE_ACCOUNT = 1510;
export const TAX_ACCOUNT = 1630;
export const PAID_F_TAX_ACCOUNT = 2518;

/**
 * The shared `CompileContext` has no period information; callers that know the
 * VAT period pass `periodKey` (see src/domain/periods/keys.ts) so it lands in
 * `meta.periodKey`. Without it the key is empty and the booking service must
 * fill it in before booking.
 */
export type CompileContextWithPeriod = CompileContext & {
  periodKey?: string | undefined;
  /** Where the proposal came from; defaults to 'manual'. */
  source?: VerifikationMeta['source'] | undefined;
};

// ---- Labels -----------------------------------------------------------------

/** Layer-0 label for a kind, per entity where the spec differs. */
export function kindLabel(kind: TransactionKind, entity: EntityType, taxSubkind?: TaxSubkind): string {
  switch (kind) {
    case 'purchase':
      return 'Köp';
    case 'sale':
      return 'Försäljning';
    case 'transfer':
      return 'Överföring';
    case 'owner_in':
      return ownerInLabel(entity);
    case 'owner_out':
      return ownerOutLabel(entity);
    case 'refund':
      return 'Återbetalning';
    case 'tax':
      return taxLabel(taxSubkind);
    case 'settle':
      return 'Betalning av faktura';
    case 'vat_settlement':
      return 'Momsredovisning';
    case 'year_end':
      return 'Bokslut';
    case 'other':
      return 'Annat';
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown transaction kind ${String(exhaustive)}`);
    }
  }
}

function ownerInLabel(entity: EntityType): string {
  switch (entity) {
    case 'enskild_firma':
      return 'Egen insättning';
    case 'aktiebolag':
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return 'Insättning eller utlägg från dig';
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

function ownerOutLabel(entity: EntityType): string {
  switch (entity) {
    case 'enskild_firma':
      return 'Eget uttag';
    case 'aktiebolag':
      return 'Privat köp – ska betalas tillbaka';
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return 'Utlägg som ska betalas tillbaka';
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

function taxLabel(subkind: TaxSubkind | undefined): string {
  switch (subkind) {
    case 'vat_payment':
      return 'Momsinbetalning';
    case 'f_tax':
      return 'F-skatt';
    case 'skattekonto_deposit':
      return 'Insättning på skattekontot';
    case undefined:
      return 'Skatt och moms';
    default: {
      const exhaustive: never = subkind;
      throw new Error(`Unknown tax subkind ${String(exhaustive)}`);
    }
  }
}

// ---- Row helpers ------------------------------------------------------------

class Rows {
  readonly rows: VerifikationRow[] = [];
  debit(accountNumber: number, amount: Ore): this {
    if (amount > 0) this.rows.push({ accountNumber, debit: amount, credit: 0 });
    return this;
  }
  credit(accountNumber: number, amount: Ore): this {
    if (amount > 0) this.rows.push({ accountNumber, debit: 0, credit: amount });
    return this;
  }
}

const kr = (ore: Ore) => `${formatSek(ore)} kr`;

/**
 * Account a payment source hits when it is on the given side. The owner
 * source is the only direction-dependent one: money from the owner (credit
 * side) is an insättning/skuld, money to the owner (debit side) an uttag/fordran.
 */
function sourceAccount(source: PaymentSource, entity: EntityType, side: 'debit' | 'credit'): number {
  if (source.kind === 'owner') return ownerAccountFor(entity, side === 'credit' ? 'in' : 'out');
  return source.accountNumber;
}

function requireSource(ctx: CompileContext, id: string, field: 'paymentSourceId' | 'counterpartySourceId'): PaymentSource {
  const source = ctx.paymentSources.get(id);
  if (!source) {
    throw new CompileError(
      field === 'paymentSourceId' ? 'unknown_payment_source' : 'missing_counterparty_source',
      field === 'paymentSourceId' ? 'Välj vad köpet betalades med.' : 'Välj vilket konto pengarna flyttades till.',
      field,
    );
  }
  return source;
}

function requireCategory(ctx: CompileContext, input: TransactionInput, kind: Category['kind']): Category {
  if (!input.categoryId) {
    throw new CompileError('missing_category', kind === 'purchase' ? 'Välj en kategori för köpet.' : 'Välj vad inkomsten gäller.', 'categoryId');
  }
  const category = findCategory(ctx.catalog, input.categoryId);
  if (!category) {
    throw new CompileError('unknown_category', `Kategorin ${input.categoryId} finns inte för den här företagsformen.`, 'categoryId');
  }
  if (category.kind !== kind) {
    throw new CompileError(
      'unknown_category',
      kind === 'purchase'
        ? `${category.label} är en inkomstkategori – välj en kategori för köp.`
        : `${category.label} är en utgiftskategori – välj en kategori för inkomster.`,
      'categoryId',
    );
  }
  return category;
}

/** The VAT choice that actually applies: unregistered companies never book VAT. */
function effectiveVat(ctx: CompileContext, vat: VatChoice): VatChoice {
  if (!ctx.vatRegistered) return { rate: 0, mode: 'none' };
  if (vat.mode === 'none') return { rate: 0, mode: 'none' };
  return vat;
}

function rcRate(vat: VatChoice): VatRate {
  return vat.rate === 0 ? 25 : vat.rate;
}

function accountsToActivate(ctx: CompileContext, rows: readonly VerifikationRow[]): number[] {
  const out = new Set<number>();
  for (const row of rows) {
    const account = ctx.accounts.get(row.accountNumber);
    if (!account || !account.active) out.add(row.accountNumber);
  }
  return [...out].sort((a, b) => a - b);
}

interface Compiled {
  rows: VerifikationRow[];
  description: string;
  explanation: string;
  vat?: VatChoice;
  categoryId?: string;
  asset?: AssetDecision;
}

function finish(input: TransactionInput, ctx: CompileContextWithPeriod, compiled: Compiled, extra: Partial<VerifikationMeta> = {}): CompileResult {
  const meta: VerifikationMeta = {
    kind: input.kind,
    categoryId: compiled.categoryId ?? input.categoryId ?? null,
    paymentSourceId: input.paymentSourceId,
    counterparty: input.counterparty,
    source: ctx.source ?? 'manual',
    reason: null,
    missingDocument: input.kind === 'purchase' && input.documentIds.length === 0,
    periodKey: ctx.periodKey ?? '',
    ...extra,
  };
  if (input.taxSubkind) meta.taxSubkind = input.taxSubkind;
  if (compiled.vat) meta.vat = compiled.vat;
  if (input.counterpartySourceId) meta.counterpartySourceId = input.counterpartySourceId;
  if (input.options?.settlesVerifikationId) meta.settlesVerifikationId = input.options.settlesVerifikationId;
  const draft: VerifikationDraft = { date: input.date, description: compiled.description, rows: compiled.rows };
  const result: CompileResult = { draft, meta, accountsToActivate: accountsToActivate(ctx, compiled.rows), explanation: compiled.explanation };
  if (compiled.asset) result.asset = compiled.asset;
  return result;
}

function describe(label: string, counterparty: string, note?: string): string {
  const who = counterparty.trim() || note?.trim() || '';
  return who ? `${label} ${who}` : label;
}

// ---- Entry point ------------------------------------------------------------

/**
 * Compile a plain-language transaction. Throws `CompileError` (Swedish
 * message) when the input cannot be turned into rows; never returns an
 * unbalanced draft.
 */
export function compileTransaction(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  if (!Number.isInteger(input.amountOre) || input.amountOre <= 0) {
    throw new CompileError('invalid_amount', 'Beloppet måste vara större än noll.', 'amountOre');
  }
  switch (input.kind) {
    case 'purchase':
      return compilePurchase(input, ctx);
    case 'sale':
      return compileSale(input, ctx);
    case 'transfer':
      return compileTransfer(input, ctx);
    case 'owner_in':
      return compileOwnerIn(input, ctx);
    case 'owner_out':
      return compileOwnerOut(input, ctx);
    case 'refund':
      return compileRefund(input, ctx);
    case 'tax':
      return compileTax(input, ctx);
    case 'settle':
      return compileSettle(input, ctx);
    case 'vat_settlement':
      throw new CompileError('unsupported_kind', 'Momsredovisningen bokförs från Momsrapporten.', 'kind');
    case 'year_end':
      throw new CompileError('unsupported_kind', 'Bokslutsposter bokförs från Bokslut.', 'kind');
    case 'other':
      throw new CompileError('unsupported_kind', 'Använd Ny verifikation för den här händelsen.', 'kind');
    default: {
      const exhaustive: never = input.kind;
      throw new CompileError('unsupported_kind', `Okänd händelsetyp ${String(exhaustive)}.`, 'kind');
    }
  }
}

// ---- Purchase ---------------------------------------------------------------

function compilePurchase(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  const source = requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  const category = requireCategory(ctx, input, 'purchase');
  const requested = input.vat ?? category.defaultVat;
  const vat = effectiveVat(ctx, requested);
  const gross = input.amountOre;
  const defer = input.options?.deferPayment === true;
  const deferVatToSettlement = defer && ctx.accountingMethod === 'cash';
  const rows = new Rows();
  const notes: string[] = [];

  // 1. Split gross into what the cost account carries and deductible VAT.
  let net = gross;
  let deductibleVat = 0;
  let rcVat = 0;
  if (vat.mode === 'domestic' && vat.rate > 0) {
    const split = splitGross(gross, vat.rate, vat.vatOre);
    net = split.netOre;
    deductibleVat = split.vatOre;
  } else if (isReverseCharge(vat.mode)) {
    rcVat = vatOnNet(gross, rcRate(vat));
  }

  // 2. Resolve the account (representation, asset threshold, foreign SaaS).
  //    The requested VAT mode decides "foreign seller", registered or not.
  const resolved: ResolvedAccount = resolveCategoryAccount(category, {
    vat: requested,
    netOre: net,
    rulesYear: ctx.rulesYear,
    persons: input.options?.persons,
    mealType: input.options?.mealType,
    assetChoice: input.options?.assetChoice,
  });
  let costAccount = resolved.accountNumber;
  let cost = net;
  // Said once on the card for every purchase the rule applies to, whichever
  // way it went — never only when it surprised someone.
  const asset = category.rule === 'asset_threshold' ? assetDecision(net, ctx.rulesYear, input.options?.assetChoice) : undefined;

  if (resolved.rule === 'representation_meal' && deductibleVat > 0) {
    const persons = Math.max(1, Math.floor(input.options?.persons ?? 1));
    const cap = representationVatCapOre(persons, vat.rate);
    const allowed = Math.min(deductibleVat, cap);
    const nonDeductible = deductibleVat - allowed;
    cost = net + nonDeductible;
    deductibleVat = allowed;
    notes.push(
      `Representation för ${persons} ${persons === 1 ? 'person' : 'personer'}: moms dras av på högst 300 kr per person (${kr(allowed)}), resten bokförs som ej avdragsgill representation.`,
    );
  } else if (resolved.rule === 'representation_meal') {
    notes.push('Representationsmåltider är inte avdragsgilla, så hela beloppet bokförs som ej avdragsgill representation.');
  } else if (resolved.rule === 'representation_light') {
    notes.push('Enklare förtäring (högst 60 kr per person) är avdragsgill och momsen dras av i sin helhet.');
  } else if (asset?.books === 'asset' && !asset.overridden) {
    notes.push(`Beloppet är minst ett halvt prisbasbelopp (${kr(asset.thresholdOre)}), så det bokförs som inventarie och skrivs av vid bokslut.`);
  } else if (asset?.books === 'asset') {
    notes.push(`Bokförs som inventarie och skrivs av vid bokslut, som du valde, trots att beloppet är under ett halvt prisbasbelopp (${kr(asset.thresholdOre)}).`);
  } else if (asset?.overridden) {
    notes.push('Bokförs som en kostnad, som du valde, trots att beloppet är minst ett halvt prisbasbelopp.');
  } else if (resolved.rule === 'foreign_saas') {
    notes.push('Utländsk säljare: bokförs som IT-tjänst med omvänd moms.');
  }

  // 3. Rows.
  if (deferVatToSettlement) {
    // Kontantmetoden: the whole invoice on the cost account, VAT split when paid.
    rows.debit(costAccount, gross).credit(SUPPLIER_INVOICE_ACCOUNT, gross);
  } else {
    rows.debit(costAccount, cost).debit(INPUT_VAT_ACCOUNT, deductibleVat);
    if (rcVat > 0) {
      rows.debit(REVERSE_CHARGE_INPUT_VAT_ACCOUNT, rcVat).credit(reverseChargeOutputAccountFor(rcRate(vat)), rcVat);
    }
    rows.credit(defer ? SUPPLIER_INVOICE_ACCOUNT : sourceAccount(source, ctx.entityType, 'credit'), gross);
  }

  // 4. Explanation.
  const label = category.label;
  const paidWith = defer ? 'Bokförs som skuld till leverantören tills fakturan betalas.' : paidWithSentence(source, ctx.entityType, gross);
  let lead: string;
  if (!ctx.vatRegistered) {
    lead = `${kr(gross)} bokförs som ${label}. Ni är inte momsregistrerade, så ingen moms dras av.`;
    if (isReverseCharge(requested.mode)) lead += ' Utländsk säljare: ingen omvänd moms redovisas när ni inte är momsregistrerade.';
  } else if (deferVatToSettlement && isReverseCharge(vat.mode)) {
    lead = `${kr(gross)} bokförs som ${basAccountName(costAccount)}. Omvänd moms (${rcRate(vat)} %) redovisas när fakturan betalas.`;
  } else if (deferVatToSettlement) {
    lead = `${kr(gross)} bokförs som ${label}. Momsen (${vat.rate} %) bokförs när fakturan betalas.`;
  } else if (rcVat > 0) {
    lead = `Omvänd moms: ${kr(gross)} bokförs som ${basAccountName(costAccount)}. Vi redovisar ${kr(rcVat)} utgående och lika mycket ingående moms, så nettot blir noll.`;
  } else if (deductibleVat > 0) {
    lead = `${kr(cost)} bokförs som ${label}, momsen ${kr(deductibleVat)} dras av.`;
  } else {
    lead = `${kr(gross)} bokförs som ${label} utan moms.`;
  }
  const explanation = [lead, ...notes, paidWith].join(' ');

  const momskod = baseMomskodFor('purchase', vat);
  const vatMeta: VatChoice = { rate: vat.rate, mode: vat.mode };
  if (deductibleVat > 0) vatMeta.vatOre = deductibleVat;
  else if (rcVat > 0) vatMeta.vatOre = rcVat;
  if (momskod) vatMeta.momskod = momskod;
  else if (deductibleVat > 0) vatMeta.momskod = '48';

  return finish(input, ctx, {
    rows: rows.rows,
    description: describe(kindLabel('purchase', ctx.entityType), input.counterparty, input.note),
    explanation,
    vat: vatMeta,
    categoryId: category.id,
    ...(asset ? { asset } : {}),
  });
}

function paidWithSentence(source: PaymentSource, entity: EntityType, gross: Ore): string {
  if (source.kind !== 'owner') return `Betalt med ${source.name}.`;
  switch (entity) {
    case 'enskild_firma':
      return `Betalt med egna pengar – bokförs som egen insättning.`;
    case 'aktiebolag':
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return `Betalt med egna pengar. ${entity === 'aktiebolag' ? 'Företaget' : 'Föreningen'} är skyldig dig ${kr(gross)}. Betala tillbaka när du vill – vi föreslår kvittningen när överföringen dyker upp.`;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

// ---- Sale -------------------------------------------------------------------

function compileSale(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  const source = requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  const category = requireCategory(ctx, input, 'sale');
  const vat = effectiveVat(ctx, input.vat ?? category.defaultVat);
  const gross = input.amountOre;
  const defer = input.options?.deferPayment === true;
  const deferVatToSettlement = defer && ctx.accountingMethod === 'cash';
  const rows = new Rows();
  const receivingAccount = defer ? CUSTOMER_INVOICE_ACCOUNT : sourceAccount(source, ctx.entityType, 'debit');

  let outputVat = 0;
  let net = gross;
  if (vat.mode === 'domestic' && vat.rate > 0 && !deferVatToSettlement) {
    const split = splitGross(gross, vat.rate, vat.vatOre);
    outputVat = split.vatOre;
    net = split.netOre;
  }
  rows.debit(receivingAccount, gross).credit(category.accountNumber, net);
  if (outputVat > 0) rows.credit(outputVatAccountFor(vat.rate), outputVat);

  let lead: string;
  if (!ctx.vatRegistered) {
    lead = `${kr(gross)} bokförs som ${category.label}. Ni är inte momsregistrerade, så ingen moms läggs på.`;
  } else if (deferVatToSettlement) {
    lead = `${kr(gross)} bokförs som ${category.label} och som kundfordran. Momsen (${vat.rate} %) bokförs när kunden betalar.`;
  } else if (outputVat > 0) {
    lead = `${kr(net)} bokförs som ${category.label}; ${kr(outputVat)} är moms som ska betalas till Skatteverket.`;
  } else if (vat.mode === 'domestic') {
    lead = `${kr(gross)} bokförs som ${category.label}, momsfri försäljning.`;
  } else if (isReverseCharge(vat.mode)) {
    lead = `${kr(gross)} bokförs som ${category.label}. Köparen redovisar momsen (omvänd betalningsskyldighet).`;
  } else {
    lead = `${kr(gross)} bokförs som ${category.label} utan moms.`;
  }
  const tail = defer ? 'Bokförs som kundfordran tills betalningen syns.' : `Pengarna kom in på ${source.name}.`;

  const vatMeta: VatChoice = { rate: vat.rate, mode: vat.mode };
  if (outputVat > 0) vatMeta.vatOre = outputVat;
  const momskod = baseMomskodFor('sale', vat);
  if (momskod) vatMeta.momskod = momskod;

  return finish(input, ctx, {
    rows: rows.rows,
    description: describe(kindLabel('sale', ctx.entityType), input.counterparty, input.note),
    explanation: `${lead} ${tail}`,
    vat: vatMeta,
    categoryId: category.id,
  });
}

// ---- Transfer ---------------------------------------------------------------

function compileTransfer(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  const from = requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  if (!input.counterpartySourceId) {
    throw new CompileError('missing_counterparty_source', 'Välj vilket konto pengarna flyttades till.', 'counterpartySourceId');
  }
  const to = requireSource(ctx, input.counterpartySourceId, 'counterpartySourceId');
  const fromAccount = sourceAccount(from, ctx.entityType, 'credit');
  const toAccount = sourceAccount(to, ctx.entityType, 'debit');
  if (fromAccount === toAccount) {
    throw new CompileError('missing_counterparty_source', `${from.name} och ${to.name} bokförs på samma konto – välj två olika.`, 'counterpartySourceId');
  }
  const gross = input.amountOre;
  const rows = new Rows().debit(toAccount, gross).credit(fromAccount, gross);
  return finish(input, ctx, {
    rows: rows.rows,
    description: `Överföring ${from.name} → ${to.name}`,
    explanation: `${kr(gross)} flyttas från ${from.name} till ${to.name}. Ingen kostnad, ingen intäkt, ingen moms.`,
  });
}

// ---- Owner ------------------------------------------------------------------

function compileOwnerIn(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  const source = requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  const gross = input.amountOre;
  const ownerAccount = ownerAccountFor(ctx.entityType, 'in');
  const rows = new Rows().debit(source.accountNumber, gross).credit(ownerAccount, gross);
  let explanation: string;
  switch (ctx.entityType) {
    case 'enskild_firma':
      explanation = `${kr(gross)} in på ${source.name} som egen insättning. Det är dina pengar, inte en intäkt.`;
      break;
    case 'aktiebolag':
      explanation = `${kr(gross)} in på ${source.name}. Bolaget är skyldigt dig ${kr(gross)} tills det betalas tillbaka.`;
      break;
    case 'ideell_forening':
    case 'bostadsrattsforening':
      explanation = `${kr(gross)} in på ${source.name}. Föreningen är skyldig dig ${kr(gross)} tills det betalas tillbaka.`;
      break;
    default: {
      const exhaustive: never = ctx.entityType;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
  return finish(input, ctx, {
    rows: rows.rows,
    description: describe(kindLabel('owner_in', ctx.entityType), input.counterparty, input.note),
    explanation,
  });
}

function compileOwnerOut(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  const source = requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  const gross = input.amountOre;
  const ownerAccount = ownerAccountFor(ctx.entityType, 'out');
  const rows = new Rows().debit(ownerAccount, gross).credit(source.accountNumber, gross);
  let explanation: string;
  switch (ctx.entityType) {
    case 'enskild_firma':
      explanation = `${kr(gross)} från ${source.name} som eget uttag. Ingen kostnad för firman.`;
      break;
    case 'aktiebolag':
      explanation = `${kr(gross)} från ${source.name} bokförs som en fordran på dig. Betala tillbaka till bolaget så snart som möjligt – lån till ägare är inte tillåtna.`;
      break;
    case 'ideell_forening':
    case 'bostadsrattsforening':
      explanation = `${kr(gross)} från ${source.name} bokförs som ett utlägg som ska betalas tillbaka till föreningen.`;
      break;
    default: {
      const exhaustive: never = ctx.entityType;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
  return finish(input, ctx, {
    rows: rows.rows,
    description: describe(kindLabel('owner_out', ctx.entityType), input.counterparty, input.note),
    explanation,
  });
}

// ---- Refund -----------------------------------------------------------------

/**
 * Mirror of the original rows. A partial refund scales every row and puts
 * the rounding remainder on the largest row of each side so the draft
 * balances exactly.
 */
function compileRefund(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  const original = ctx.originalRows;
  if (!original || original.length < 2) {
    throw new CompileError('missing_original', 'Välj vilket köp som betalades tillbaka.', 'options');
  }
  const originalTotal = original.reduce((sum, r) => sum + r.debit, 0);
  if (originalTotal <= 0) throw new CompileError('missing_original', 'Det ursprungliga köpet saknar belopp.', 'options');
  const amount = input.amountOre;
  if (amount > originalTotal) {
    throw new CompileError('invalid_amount', `Återbetalningen (${kr(amount)}) är större än det ursprungliga köpet (${kr(originalTotal)}).`, 'amountOre');
  }
  const mirrored = reverseRows(original);
  const rows = amount === originalTotal ? mirrored : scaleRows(mirrored, amount, originalTotal);
  const partial = amount !== originalTotal;
  return finish(
    input,
    ctx,
    {
      rows,
      description: describe(kindLabel('refund', ctx.entityType), input.counterparty, input.note),
      explanation: partial
        ? `${kr(amount)} av ${kr(originalTotal)} betalas tillbaka. Kostnaden och momsen minskar i samma proportion som det ursprungliga köpet.`
        : `Hela köpet på ${kr(amount)} betalas tillbaka. Kostnaden och momsen vänds tillbaka precis som de bokfördes.`,
    },
    input.options?.refundOfVerifikationId ? { settlesVerifikationId: input.options.refundOfVerifikationId } : {},
  );
}

function scaleRows(rows: readonly VerifikationRow[], amount: Ore, total: Ore): VerifikationRow[] {
  const scaled = rows.map((r) => ({
    ...r,
    debit: Math.round((r.debit * amount) / total),
    credit: Math.round((r.credit * amount) / total),
  }));
  const fix = (side: 'debit' | 'credit') => {
    const sum = scaled.reduce((s, r) => s + r[side], 0);
    const diff = amount - sum;
    if (diff === 0) return;
    let largest = -1;
    scaled.forEach((r, i) => {
      if (r[side] > 0 && (largest < 0 || r[side] > scaled[largest]![side])) largest = i;
    });
    if (largest >= 0) scaled[largest]![side] += diff;
  };
  fix('debit');
  fix('credit');
  return scaled.filter((r) => r.debit > 0 || r.credit > 0);
}

// ---- Tax --------------------------------------------------------------------

function compileTax(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  const source = requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  if (!input.taxSubkind) {
    throw new CompileError('missing_tax_subkind', 'Vad var det här? Välj moms, F-skatt eller insättning på skattekontot.', 'taxSubkind');
  }
  const gross = input.amountOre;
  const payingAccount = sourceAccount(source, ctx.entityType, 'credit');
  const rows = new Rows();
  let explanation: string;
  switch (input.taxSubkind) {
    case 'f_tax': {
      const debitAccount = fTaxAccountFor(ctx.entityType);
      rows.debit(debitAccount, gross).credit(payingAccount, gross);
      explanation =
        ctx.entityType === 'enskild_firma'
          ? `F-skatten är din privata skatt – ${kr(gross)} bokförs som eget uttag, inte som kostnad.`
          : `${kr(gross)} bokförs som betald F-skatt (preliminärskatt) och stäms av mot årets skatt i bokslutet.`;
      break;
    }
    case 'vat_payment': {
      switch (ctx.entityType) {
        case 'enskild_firma':
          rows.debit(VAT_SETTLEMENT_ACCOUNT, gross).credit(payingAccount, gross);
          explanation = `${kr(gross)} betalas till Skatteverket och minskar momsskulden från momsredovisningen.`;
          break;
        case 'aktiebolag':
        case 'ideell_forening':
        case 'bostadsrattsforening':
          if (payingAccount === TAX_ACCOUNT) {
            rows.debit(VAT_SETTLEMENT_ACCOUNT, gross).credit(TAX_ACCOUNT, gross);
            explanation = `${kr(gross)} avräknas från skattekontot mot momsskulden.`;
          } else {
            rows.debit(TAX_ACCOUNT, gross).debit(VAT_SETTLEMENT_ACCOUNT, gross).credit(payingAccount, gross).credit(TAX_ACCOUNT, gross);
            explanation = `${kr(gross)} går via skattekontot: pengarna sätts in där och avräknas sedan mot momsskulden, så skattekontot syns i bokföringen.`;
          }
          break;
        default: {
          const exhaustive: never = ctx.entityType;
          throw new Error(`Unknown entity type ${String(exhaustive)}`);
        }
      }
      break;
    }
    case 'skattekonto_deposit': {
      switch (ctx.entityType) {
        case 'enskild_firma':
          rows.debit(ownerAccountFor('enskild_firma', 'out'), gross).credit(payingAccount, gross);
          explanation = `Skattekontot är ditt privata – insättningen på ${kr(gross)} bokförs som eget uttag.`;
          break;
        case 'aktiebolag':
        case 'ideell_forening':
        case 'bostadsrattsforening':
          rows.debit(TAX_ACCOUNT, gross).credit(payingAccount, gross);
          explanation = `${kr(gross)} sätts in på skattekontot och ligger kvar som en fordran på Skatteverket tills den avräknas.`;
          break;
        default: {
          const exhaustive: never = ctx.entityType;
          throw new Error(`Unknown entity type ${String(exhaustive)}`);
        }
      }
      break;
    }
    default: {
      const exhaustive: never = input.taxSubkind;
      throw new CompileError('missing_tax_subkind', `Okänd skattetyp ${String(exhaustive)}.`, 'taxSubkind');
    }
  }
  return finish(input, ctx, {
    rows: rows.rows,
    description: describe(kindLabel('tax', ctx.entityType, input.taxSubkind), input.counterparty || 'Skatteverket', input.note),
    explanation,
  });
}

/** Where paid F-skatt lands: EF = eget uttag (2013), juridiska personer = 2518. */
export function fTaxAccountFor(entity: EntityType): number {
  switch (entity) {
    case 'enskild_firma':
      return ownerAccountFor('enskild_firma', 'out');
    case 'aktiebolag':
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return PAID_F_TAX_ACCOUNT;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

// ---- Settle -----------------------------------------------------------------

/**
 * Payment of a supplier (2440) or customer (1510) invoice. Under
 * kontantmetoden the invoice was booked gross, so the settlement carries the
 * VAT rows (`input.vat` = the invoice's VAT choice): the domestic split
 * (D 2640 V / K CAT V, with the representation cap when the invoice sits on
 * 6072) or the reverse-charge pair (D 2645 / K 26x4 at 25 %·G, boxes
 * 20/21/22). Partial payments carry a proportional share.
 */
function compileSettle(input: TransactionInput, ctx: CompileContextWithPeriod): CompileResult {
  const source = requireSource(ctx, input.paymentSourceId, 'paymentSourceId');
  const original = ctx.originalRows;
  if (!original || original.length < 2) {
    throw new CompileError('missing_original', 'Välj vilken faktura som betalades.', 'options');
  }
  const supplier = original.some((r) => r.accountNumber === SUPPLIER_INVOICE_ACCOUNT && r.credit > 0);
  const customer = !supplier && original.some((r) => r.accountNumber === CUSTOMER_INVOICE_ACCOUNT && r.debit > 0);
  if (!supplier && !customer) {
    throw new CompileError('missing_original', 'Den valda händelsen är ingen obetald faktura.', 'options');
  }
  const gross = input.amountOre;
  const rows = new Rows();
  const vat = pendingSettlementVat(ctx, input.vat);
  const invoiceHadVatRows = original.some((r) => isVatClearingAccount(r.accountNumber));
  const pendingVat = ctx.accountingMethod === 'cash' && vat !== undefined && !invoiceHadVatRows;
  const invoiceTotal = original.reduce((sum, r) => sum + r.debit, 0);
  const partial = gross < invoiceTotal;
  let explanation: string;
  let vatMeta: VatChoice | undefined;

  if (supplier) {
    rows.debit(SUPPLIER_INVOICE_ACCOUNT, gross).credit(sourceAccount(source, ctx.entityType, 'credit'), gross);
    explanation = `${kr(gross)} betalas till leverantören från ${source.name}; skulden från fakturan försvinner.`;
    if (pendingVat && vat) {
      const costRow = original.filter((r) => r.debit > 0 && r.accountNumber !== SUPPLIER_INVOICE_ACCOUNT).sort((a, b) => b.debit - a.debit)[0];
      if (costRow && isReverseCharge(vat.mode)) {
        // Reverse charge: the cost stays gross; report and deduct 25 %·G now.
        const rate = rcRate(vat);
        const rcVat = vatOnNet(gross, rate);
        rows.debit(REVERSE_CHARGE_INPUT_VAT_ACCOUNT, rcVat).credit(reverseChargeOutputAccountFor(rate), rcVat);
        vatMeta = { rate, mode: vat.mode, vatOre: rcVat };
        const momskod = baseMomskodFor('purchase', vat);
        if (momskod) vatMeta.momskod = momskod;
        explanation += ` Omvänd moms: vi redovisar ${kr(rcVat)} utgående och lika mycket ingående moms nu när fakturan är betald (kontantmetoden), så nettot blir noll.`;
      } else if (costRow) {
        let vatOre = partial ? vatFromGross(gross, vat.rate) : splitGross(gross, vat.rate, vat.vatOre).vatOre;
        let capNote = '';
        if (costRow.accountNumber === REPRESENTATION_NON_DEDUCTIBLE_ACCOUNT) {
          // Representation meal booked gross on 6072: only 300 kr/person of VAT base
          // is deductible; a partial payment gets its share of the cap.
          const persons = Math.max(1, Math.floor(input.options?.persons ?? 1));
          const fullCap = representationVatCapOre(persons, vat.rate);
          const cap = partial ? Math.round((fullCap * gross) / invoiceTotal) : fullCap;
          if (vatOre > cap) {
            vatOre = cap;
            capNote = ` Representation för ${persons} ${persons === 1 ? 'person' : 'personer'}: moms dras av på högst 300 kr per person, resten ligger kvar som ej avdragsgill representation.`;
          }
        }
        rows.debit(INPUT_VAT_ACCOUNT, vatOre).credit(costRow.accountNumber, vatOre);
        vatMeta = { rate: vat.rate, mode: vat.mode, vatOre, momskod: '48' };
        explanation += ` Momsen ${kr(vatOre)} dras av nu när fakturan är betald (kontantmetoden).${capNote}`;
      }
    }
  } else {
    rows.debit(sourceAccount(source, ctx.entityType, 'debit'), gross).credit(CUSTOMER_INVOICE_ACCOUNT, gross);
    explanation = `${kr(gross)} från kunden kom in på ${source.name}; kundfordran försvinner.`;
    if (pendingVat && vat && !isReverseCharge(vat.mode)) {
      const revenueRow = original.filter((r) => r.credit > 0 && r.accountNumber !== CUSTOMER_INVOICE_ACCOUNT).sort((a, b) => b.credit - a.credit)[0];
      if (revenueRow) {
        const vatOre = partial ? vatFromGross(gross, vat.rate) : splitGross(gross, vat.rate, vat.vatOre).vatOre;
        rows.debit(revenueRow.accountNumber, vatOre).credit(outputVatAccountFor(vat.rate), vatOre);
        vatMeta = { rate: vat.rate, mode: vat.mode, vatOre, momskod: '05' };
        explanation += ` Momsen ${kr(vatOre)} redovisas nu när kunden har betalat (kontantmetoden).`;
      }
    } else if (pendingVat && vat) {
      // Reverse-charge sale: no VAT rows, but the settlement is what gets reported (box 35/39/40).
      vatMeta = { rate: vat.rate, mode: vat.mode };
      const momskod = baseMomskodFor('sale', vat);
      if (momskod) vatMeta.momskod = momskod;
      explanation += ' Köparen redovisar momsen (omvänd betalningsskyldighet); försäljningen redovisas nu när kunden har betalat.';
    }
  }
  if (settlementVatUnknown(ctx, input.vat, invoiceHadVatRows)) {
    // Kontantmetoden invoice booked gross, but the caller did not pass the
    // invoice's VAT choice: nothing is booked silently, say so in the notes.
    explanation += supplier
      ? ' Fakturans moms är inte angiven, så betalningen bokförs utan moms. Ange momsen på fakturan om den ska dras av nu (kontantmetoden).'
      : ' Fakturans moms är inte angiven, så betalningen bokförs utan moms. Ange momsen på fakturan om den ska redovisas nu (kontantmetoden).';
  }
  if (partial) explanation += ` Delbetalning: ${kr(invoiceTotal - gross)} återstår.`;

  const compiled: Compiled = {
    rows: rows.rows,
    description: describe(kindLabel('settle', ctx.entityType), input.counterparty, input.note),
    explanation,
  };
  if (vatMeta) compiled.vat = vatMeta;
  return finish(input, ctx, compiled);
}

/**
 * A kontantmetoden settlement of a gross-booked invoice whose VAT choice the
 * caller omitted: the compiler cannot know the rate, so it books no VAT and
 * the explanation says so. Unregistered companies have nothing to book.
 */
export function settlementVatUnknown(
  ctx: Pick<CompileContext, 'accountingMethod' | 'vatRegistered'>,
  vat: VatChoice | undefined,
  invoiceHadVatRows: boolean,
): boolean {
  return ctx.accountingMethod === 'cash' && ctx.vatRegistered && vat === undefined && !invoiceHadVatRows;
}

/**
 * The invoice VAT a kontantmetoden settlement still has to book: a domestic
 * rate above zero or any reverse-charge mode. Unregistered companies and
 * `mode: 'none'` invoices have nothing pending.
 */
function pendingSettlementVat(ctx: CompileContext, vat: VatChoice | undefined): VatChoice | undefined {
  if (!vat || !ctx.vatRegistered) return undefined;
  switch (vat.mode) {
    case 'domestic':
      return vat.rate > 0 ? vat : undefined;
    case 'none':
      return undefined;
    case 'rc_services_eu':
    case 'rc_services_non_eu':
    case 'rc_goods_eu':
      return vat;
    default: {
      const exhaustive: never = vat.mode;
      throw new Error(`Unknown VAT mode ${String(exhaustive)}`);
    }
  }
}
